import { useCallback, useEffect, useRef, useState } from 'react';

import { useToast } from '@ecoinvent/ui';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import useVersionInfo from 'hooks/useVersionInfo';
import { isV4Release } from 'utilities/constants/versions';

import {
  collectNodesToRemove,
  countsFromNodes,
  DerivedEdge,
  edgeId,
  expansionOwnerId,
  fetchNeighborhood,
  makeInputEdges,
  makeOutputEdges,
  MAX_NODES_PER_EXPANSION,
  mergeEdges,
  mergeNodes,
  NeighborhoodPageParams,
  NodeCounts,
  resolveExpansionInstances,
  ShownCounts,
} from './supplyChainUtils';
import { SupplyChainNeighborhood, SupplyChainNode, SupplyChainPage } from './types';

export type { DerivedEdge, NodeCounts, ShownCounts };

// Page 0 of both sides: this response is cached per node and reused by both directions, so neither limit
// can be trimmed here.
const FIRST_PAGE_PARAMS: NeighborhoodPageParams = {
  inputs_offset: 0,
  inputs_limit: MAX_NODES_PER_EXPANSION,
  outputs_offset: 0,
  outputs_limit: MAX_NODES_PER_EXPANSION,
};

// One page of a single direction. Both envelopes always come back, so the unused side is asked for the
// API minimum of 1 (0 is a 422); `total` does not depend on `limit`, so its count stays correct.
const pageParams = (direction: 'inputs' | 'outputs', offset: number): NeighborhoodPageParams =>
  direction === 'inputs'
    ? { inputs_offset: offset, inputs_limit: MAX_NODES_PER_EXPANSION, outputs_limit: 1 }
    : { outputs_offset: offset, outputs_limit: MAX_NODES_PER_EXPANSION, inputs_limit: 1 };

const ERROR_TOAST_TITLE = 'Oh no...';

// Chakra's store appends unconditionally, so the `id` only dedupes when paired with `toast.isActive`:
// on its own it renders a second toast that shares the first one's React key.
const EXPAND_ERROR_TOAST = {
  id: 'supply-chain-expand-error',
  title: ERROR_TOAST_TITLE,
  description: 'There was an error expanding this activity. Please try again later.',
  status: 'error',
  isClosable: true,
} as const;

const SHOW_MORE_ERROR_TOAST = {
  id: 'supply-chain-show-more-error',
  title: ERROR_TOAST_TITLE,
  description: 'There was an error loading more activities. Please try again later.',
  status: 'error',
  isClosable: true,
} as const;

export const useSupplyChain = (datasetId?: string) => {
  const { ver: version, system_model } = useVersionInfo();
  const toast = useToast();

  // Only v4 releases serve the nodes endpoint, so the graph never loads — or fetches — on 3.x.
  const isV4 = isV4Release(version);

  const { data: initialNeighborhood, isLoading } = useQuery<SupplyChainNeighborhood, AxiosError>({
    queryKey: ['supply-chain', datasetId, version, system_model],
    queryFn: () => fetchNeighborhood(datasetId as string, version, system_model, FIRST_PAGE_PARAMS),
    enabled: isV4 && !!datasetId,
    retry: false,
  });

  const initialized = useRef(false);
  const neighborhoodCache = useRef<Record<string, SupplyChainNeighborhood>>({});
  const addedByExpansion = useRef<Record<string, Set<string>>>({});
  // Edges drawn to cards already on canvas belong to no node, so this map is the only way to collapse them.
  const edgesByExpansion = useRef<Record<string, Set<string>>>({});
  const nodesRef = useRef<SupplyChainNode[]>([]);
  const edgesRef = useRef<DerivedEdge[]>([]);
  // How many items the server has returned per `${nodeId}_inputs` / `${nodeId}_outputs` — i.e. the next
  // page offset. Deliberately not `addedByExpansion[key].size`, which undercounts: duplicates add no card.
  const fetchedCounts = useRef<Record<string, number>>({});
  // Freshest `total` the server reported per expansion key. `neighborhoodCache` only ever holds page 0, so its
  // totals go stale; the "+N more" gate reads these instead.
  const serverTotals = useRef<Record<string, number>>({});
  // Expansions currently being fetched/applied — blocks a second concurrent run of the same one.
  const inFlightExpansions = useRef<Set<string>>(new Set());
  // Expansions whose first collapse click kept the sticky children — a second click closes them.
  const partiallyCollapsed = useRef<Set<string>>(new Set());
  // Bumped whenever the whole graph is rebuilt (dataset/version change, collapseAll).
  const generation = useRef(0);
  // Bumped per expansion key whenever that key's branch is torn down by a single collapse.
  const collapseEpochs = useRef<Record<string, number>>({});

  const [nodes, setNodes] = useState<SupplyChainNode[]>([]);
  const [edges, setEdges] = useState<DerivedEdge[]>([]);
  const [nodeCounts, setNodeCounts] = useState<NodeCounts>({});
  const [shownCounts, setShownCounts] = useState<ShownCounts>({});
  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<string>>(new Set());
  const [expandedOutputNodeIds, setExpandedOutputNodeIds] = useState<Set<string>>(new Set());
  const pendingFetches = useRef(0);
  const [isExpanding, setIsExpanding] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  const rootNodeId = initialNeighborhood?.root.id ?? '';

  useEffect(() => {
    generation.current += 1;
    initialized.current = false;
    neighborhoodCache.current = {};
    addedByExpansion.current = {};
    edgesByExpansion.current = {};
    nodesRef.current = [];
    edgesRef.current = [];
    fetchedCounts.current = {};
    serverTotals.current = {};
    collapseEpochs.current = {};
    inFlightExpansions.current = new Set();
    partiallyCollapsed.current = new Set();
    setNodes([]);
    setEdges([]);
    setNodeCounts({});
    setShownCounts({});
    setExpandedNodeIds(new Set());
    setExpandedOutputNodeIds(new Set());
  }, [datasetId, version, system_model]);

  // Also re-runs on collapseAll, via resetKey.
  useEffect(() => {
    if (!initialNeighborhood || initialized.current) return;
    initialized.current = true;

    addedByExpansion.current = {};
    edgesByExpansion.current = {};
    fetchedCounts.current = {};
    serverTotals.current = {};
    collapseEpochs.current = {};
    inFlightExpansions.current = new Set();
    partiallyCollapsed.current = new Set();

    const { root, inputs, outputs } = initialNeighborhood;
    neighborhoodCache.current[root.id] = initialNeighborhood;

    // The server already returns the top MAX_NODES_PER_EXPANSION by amount, in amount-descending order.
    const visibleInputs = inputs.results;
    const visibleOutputs = outputs.results;
    const initInputEdges = makeInputEdges(root.id, visibleInputs);
    const initOutputEdges = makeOutputEdges(root.id, visibleOutputs);

    addedByExpansion.current[`${root.id}_inputs`] = new Set(visibleInputs.map((n) => n.id));
    addedByExpansion.current[`${root.id}_outputs`] = new Set(visibleOutputs.map((n) => n.id));
    edgesByExpansion.current[`${root.id}_inputs`] = new Set(initInputEdges.map((e) => e.id));
    edgesByExpansion.current[`${root.id}_outputs`] = new Set(initOutputEdges.map((e) => e.id));
    fetchedCounts.current[`${root.id}_inputs`] = visibleInputs.length;
    fetchedCounts.current[`${root.id}_outputs`] = visibleOutputs.length;
    serverTotals.current[`${root.id}_inputs`] = inputs.total;
    serverTotals.current[`${root.id}_outputs`] = outputs.total;

    // A dataset that is both an input and a consumer of root must yield a single card.
    const initNodes = mergeNodes([root, ...visibleInputs], visibleOutputs);
    const initEdges = [...initInputEdges, ...initOutputEdges];
    nodesRef.current = initNodes;
    edgesRef.current = initEdges;
    setNodes(initNodes);
    setEdges(initEdges);
    // Root last: it has no own counts, so it keeps the derivation even if it is also one of its own inputs.
    setNodeCounts({
      ...countsFromNodes([...visibleInputs, ...visibleOutputs]),
      [root.id]: { inputs: inputs.total, outputs: outputs.total },
    });
    setShownCounts({ [root.id]: { inputs: visibleInputs.length, outputs: visibleOutputs.length } });
    setExpandedNodeIds(new Set([root.id]));
    setExpandedOutputNodeIds(new Set([root.id]));
  }, [initialNeighborhood, resetKey]);

  // Identity of the graph context a fetch belongs to. A page whose stamp changed while it was in flight
  // outlived its context: applying it would re-add torn-down cards and desync the paging offsets.
  const expansionStamp = useCallback((key: string) => `${generation.current}:${collapseEpochs.current[key] ?? 0}`, []);

  // The ref updates synchronously, so two expansions resolving in the same tick read each other's cards.
  const setNodesSynced = useCallback((updater: (prev: SupplyChainNode[]) => SupplyChainNode[]) => {
    const next = updater(nodesRef.current);
    nodesRef.current = next;
    setNodes(next);
  }, []);

  const setEdgesSynced = useCallback((updater: (prev: DerivedEdge[]) => DerivedEdge[]) => {
    const next = updater(edgesRef.current);
    edgesRef.current = next;
    setEdges(next);
  }, []);

  // Single choke point for every repopulating path, so partiallyCollapsed is cleared here and not per-callback.
  const recordExpansion = useCallback((key: string, nodeIds: string[], edgeIds: string[]) => {
    partiallyCollapsed.current.delete(key);

    const nodeSet = addedByExpansion.current[key] ?? new Set<string>();
    nodeIds.forEach((id) => nodeSet.add(id));
    addedByExpansion.current[key] = nodeSet;

    const edgeSet = edgesByExpansion.current[key] ?? new Set<string>();
    edgeIds.forEach((id) => edgeSet.add(id));
    edgesByExpansion.current[key] = edgeSet;
  }, []);

  const isAncestorInExpansion = useCallback((candidateId: string, nodeId: string): boolean => {
    const visited = new Set<string>();
    const queue = [nodeId];
    while (queue.length > 0) {
      const current = queue.shift();
      if (current === undefined || visited.has(current)) continue;
      visited.add(current);
      for (const [key, nodeSet] of Object.entries(addedByExpansion.current)) {
        if (nodeSet.has(current)) {
          const parentId = expansionOwnerId(key);
          if (parentId === candidateId) return true;
          queue.push(parentId);
        }
      }
    }
    return false;
  }, []);

  // Column depth of a node: root = 0, input expansions go –1, output expansions go +1.
  const getDepth = useCallback((id: string): number => {
    for (const [key, nodeSet] of Object.entries(addedByExpansion.current)) {
      if (nodeSet.has(id)) {
        const parentId = expansionOwnerId(key);
        const direction = key.endsWith('_inputs') ? -1 : 1;
        return getDepth(parentId) + direction;
      }
    }
    return 0; // root or pre-existing node
  }, []);

  const getOrFetchNeighborhood = useCallback(
    async (nodeId: string): Promise<SupplyChainNeighborhood | undefined> => {
      if (!isV4) return undefined;
      if (neighborhoodCache.current[nodeId]) return neighborhoodCache.current[nodeId];
      const gen = generation.current;
      try {
        const data = await fetchNeighborhood(nodeId, version, system_model, FIRST_PAGE_PARAMS);
        // Caching a response fetched for a dataset/version the user has already left would outlive its context.
        if (gen === generation.current) neighborhoodCache.current[nodeId] = data;
        return data;
      } catch {
        // Callers bail out on `undefined`, which on its own leaves the click with no visible outcome.
        if (!toast.isActive(EXPAND_ERROR_TOAST.id)) toast(EXPAND_ERROR_TOAST);
        return undefined;
      }
    },
    [isV4, version, system_model, toast]
  );

  // Applies one server page to the canvas. Shared by expandNode / expandOutputs / showMoreInputs /
  // showMoreOutputs so the four paths cannot drift apart again.
  const applyPage = useCallback(
    (nodeId: string, direction: 'inputs' | 'outputs', page: SupplyChainPage) => {
      const key = `${nodeId}_${direction}`;
      const isInputs = direction === 'inputs';

      // `existingIds` is read here, after the caller's await, so it reflects expansions that resolved meanwhile.
      const { nodesToAdd, instances } = resolveExpansionInstances(
        nodeId,
        page.results,
        new Set(nodesRef.current.map((n) => n.id)),
        getDepth(nodeId) + (isInputs ? -1 : 1), // inputs go one column left, outputs one right
        (candidateId) => isAncestorInExpansion(candidateId, nodeId),
        getDepth
      );
      // Instances include nodes already on canvas, which add no card but still need their edge.
      const pageEdges = isInputs ? makeInputEdges(nodeId, instances) : makeOutputEdges(nodeId, instances);

      // Even if every item is already on canvas, the expansion is still recorded so the UI can collapse it.
      recordExpansion(
        key,
        nodesToAdd.map((n) => n.id),
        pageEdges.map((e) => e.id)
      );
      // The next offset counts what the server returned, not the cards added. An empty page means the list
      // shrank server-side, so jump to `total` rather than leave an inert "+N more" pill.
      const fetched = page.results.length > 0 ? page.offset + page.results.length : page.total;
      fetchedCounts.current[key] = fetched;
      serverTotals.current[key] = page.total;
      // Partial-collapse survivors can sit outside the paged window, so the window alone inflates "+N more".
      const shown = Math.max(fetched, addedByExpansion.current[key]?.size ?? 0);

      setNodesSynced((prev) => mergeNodes(prev, nodesToAdd));
      setEdgesSynced((prev) => mergeEdges(prev, pageEdges));
      // `prev` wins so child seeding never overwrites a node's own counts, but the focal direction is
      // refreshed from this page: the "+N more" number and the offset gate must read the same total.
      setNodeCounts((prev) => ({
        ...countsFromNodes(nodesToAdd),
        ...prev,
        // Spread, not `?? 0`: the other direction may still be unknown, and `0` would delete its pill.
        [nodeId]: { ...prev[nodeId], ...(isInputs ? { inputs: page.total } : { outputs: page.total }) },
      }));
      setShownCounts((prev) => ({
        ...prev,
        [nodeId]: {
          inputs: isInputs ? shown : (prev[nodeId]?.inputs ?? 0),
          outputs: isInputs ? (prev[nodeId]?.outputs ?? 0) : shown,
        },
      }));
    },
    [getDepth, isAncestorInExpansion, recordExpansion, setEdgesSynced, setNodesSynced]
  );

  const expandNode = useCallback(
    async (nodeId: string) => {
      const key = `${nodeId}_inputs`;
      // The in-flight guard also covers double-clicks, which pass the `expandedNodeIds` check captured here.
      if (expandedNodeIds.has(nodeId) || inFlightExpansions.current.has(key)) return;
      inFlightExpansions.current.add(key);
      const stamp = expansionStamp(key);
      // nodeId may be a composite key `${datasetId}#${parentId}` for duplicate nodes.
      const datasetId = nodeId.split('#')[0];
      const needsFetch = !neighborhoodCache.current[datasetId];
      if (needsFetch) {
        pendingFetches.current += 1;
        setIsExpanding(true);
      }
      try {
        const neighborhood = await getOrFetchNeighborhood(datasetId);
        if (!neighborhood) return;
        if (stamp !== expansionStamp(key)) return;

        // The focal node's totals are the unpaged ones; new cards are seeded from their own counts in applyPage.
        setNodeCounts((prev) => ({
          ...prev,
          [nodeId]: { inputs: neighborhood.inputs.total, outputs: neighborhood.outputs.total },
        }));
        applyPage(nodeId, 'inputs', neighborhood.inputs);
        setExpandedNodeIds((prev) => new Set([...prev, nodeId]));
      } finally {
        inFlightExpansions.current.delete(key);
        // In `finally`: a throw past the counter would leave the full-canvas overlay up permanently.
        if (needsFetch) {
          pendingFetches.current -= 1;
          if (pendingFetches.current === 0) setIsExpanding(false);
        }
      }
    },
    [applyPage, expandedNodeIds, expansionStamp, getOrFetchNeighborhood]
  );

  const collapseExpansion = useCallback(
    (nodeId: string, direction: 'inputs' | 'outputs', activeNodeId?: string | null) => {
      const key = `${nodeId}_${direction}`;
      const expanded = direction === 'inputs' ? expandedNodeIds : expandedOutputNodeIds;
      if (!expanded.has(nodeId)) return;

      const directIds = [...(addedByExpansion.current[key] ?? new Set<string>())];
      const branch = collectNodesToRemove(directIds, addedByExpansion.current, new Set([nodeId]));

      // Two-stage collapse: the first click keeps sticky direct children, a second click closes them too.
      const isSticky = (id: string) => id === activeNodeId || expandedNodeIds.has(id) || expandedOutputNodeIds.has(id);
      const keep = partiallyCollapsed.current.has(key)
        ? new Set<string>()
        : new Set(directIds.filter((id) => branch.has(id) && isSticky(id)));
      const toRemove = new Set([...branch].filter((id) => !keep.has(id)));

      // Expansions owned by the sticky survivors are torn down too: the rest of their branch is gone.
      const tornDownKeys = [key, ...Object.keys(addedByExpansion.current).filter((k) => branch.has(expansionOwnerId(k)))];
      const ownedEdgeIds = new Set(tornDownKeys.flatMap((k) => [...(edgesByExpansion.current[k] ?? [])]));

      tornDownKeys.forEach((k) => {
        delete addedByExpansion.current[k];
        delete edgesByExpansion.current[k];
        partiallyCollapsed.current.delete(k);
        collapseEpochs.current[k] = (collapseEpochs.current[k] ?? 0) + 1;
      });
      // Accepted trade-off: `keep` ⊆ `branch`, so a sticky survivor's in-flight expansion is discarded with no
      // feedback — a dead click recoverable by a second one, versus stranding the survivor's torn-down children.
      branch.forEach((id) => {
        // Both directions of every removed card, including keys with no expansion yet: one may be in flight.
        (['inputs', 'outputs'] as const).forEach((dir) => {
          delete fetchedCounts.current[`${id}_${dir}`];
          delete serverTotals.current[`${id}_${dir}`];
          collapseEpochs.current[`${id}_${dir}`] = (collapseEpochs.current[`${id}_${dir}`] ?? 0) + 1;
        });
      });

      if (keep.size > 0) {
        // Re-attach the sticky children to the parent so the second click can close them.
        addedByExpansion.current[key] = new Set(keep);
        edgesByExpansion.current[key] = new Set(
          [...keep].map((id) => (direction === 'inputs' ? edgeId(id, nodeId) : edgeId(nodeId, id)))
        );
        partiallyCollapsed.current.add(key);
        // Offset restarts at 0: survivors need not be the first items, so resuming past them would strand
        // earlier ones. Re-fetched duplicates are absorbed by the node/edge id dedupe.
        fetchedCounts.current[key] = 0;
      } else {
        delete fetchedCounts.current[key];
        delete serverTotals.current[key];
      }

      const liveEdgeIds = new Set(Object.values(edgesByExpansion.current).flatMap((ids) => [...ids]));
      const removeEdgeIds = new Set<string>();
      edgesRef.current.forEach((e) => {
        if (toRemove.has(e.source_id) || toRemove.has(e.target_id)) removeEdgeIds.add(e.id);
      });
      // Edges to cards another branch keeps visible also go, unless a still-open expansion draws them.
      ownedEdgeIds.forEach((id) => {
        if (!liveEdgeIds.has(id)) removeEdgeIds.add(id);
      });
      Object.values(edgesByExpansion.current).forEach((ids) => removeEdgeIds.forEach((id) => ids.delete(id)));

      setNodesSynced((prev) => prev.filter((n) => !toRemove.has(n.id)));
      setEdgesSynced((prev) => prev.filter((e) => !removeEdgeIds.has(e.id)));
      setShownCounts((prev) => {
        const next = { ...prev };
        toRemove.forEach((id) => delete next[id]);
        keep.forEach((id) => {
          next[id] = { inputs: 0, outputs: 0 };
        });
        // Only the sticky survivors are still on canvas, so the "+N more" badge counts those.
        next[nodeId] = {
          inputs: direction === 'inputs' ? keep.size : (prev[nodeId]?.inputs ?? 0),
          outputs: direction === 'outputs' ? keep.size : (prev[nodeId]?.outputs ?? 0),
        };
        return next;
      });
      setNodeCounts((prev) => {
        const next = { ...prev };
        toRemove.forEach((id) => delete next[id]);
        return next;
      });
      setExpandedNodeIds((prev) => {
        const next = new Set(prev);
        toRemove.forEach((id) => next.delete(id));
        keep.forEach((id) => next.delete(id));
        if (direction === 'inputs' && keep.size === 0) next.delete(nodeId);
        return next;
      });
      setExpandedOutputNodeIds((prev) => {
        const next = new Set(prev);
        toRemove.forEach((id) => next.delete(id));
        keep.forEach((id) => next.delete(id));
        if (direction === 'outputs' && keep.size === 0) next.delete(nodeId);
        return next;
      });
    },
    [expandedNodeIds, expandedOutputNodeIds, setEdgesSynced, setNodesSynced]
  );

  const collapseNode = useCallback(
    (nodeId: string, activeNodeId?: string | null) => collapseExpansion(nodeId, 'inputs', activeNodeId),
    [collapseExpansion]
  );

  const expandOutputs = useCallback(
    async (nodeId: string) => {
      const key = `${nodeId}_outputs`;
      if (expandedOutputNodeIds.has(nodeId) || inFlightExpansions.current.has(key)) return;
      inFlightExpansions.current.add(key);
      const stamp = expansionStamp(key);
      const datasetId = nodeId.split('#')[0];
      const needsFetch = !neighborhoodCache.current[datasetId];
      if (needsFetch) {
        pendingFetches.current += 1;
        setIsExpanding(true);
      }
      try {
        const neighborhood = await getOrFetchNeighborhood(datasetId);
        if (!neighborhood) return;
        if (stamp !== expansionStamp(key)) return;

        // See expandNode: the focal totals are the unpaged ones.
        setNodeCounts((prev) => ({
          ...prev,
          [nodeId]: { inputs: neighborhood.inputs.total, outputs: neighborhood.outputs.total },
        }));
        applyPage(nodeId, 'outputs', neighborhood.outputs);
        setExpandedOutputNodeIds((prev) => new Set([...prev, nodeId]));
      } finally {
        inFlightExpansions.current.delete(key);
        // See expandNode: decrementing outside `finally` can strand the overlay over the whole canvas.
        if (needsFetch) {
          pendingFetches.current -= 1;
          if (pendingFetches.current === 0) setIsExpanding(false);
        }
      }
    },
    [applyPage, expandedOutputNodeIds, expansionStamp, getOrFetchNeighborhood]
  );

  const collapseOutputs = useCallback(
    (nodeId: string, activeNodeId?: string | null) => collapseExpansion(nodeId, 'outputs', activeNodeId),
    [collapseExpansion]
  );

  const showMore = useCallback(
    async (nodeId: string, direction: 'inputs' | 'outputs') => {
      const key = `${nodeId}_${direction}`;
      if (inFlightExpansions.current.has(key)) return;

      const datasetId = nodeId.split('#')[0];
      const cached = neighborhoodCache.current[datasetId];
      if (!cached) return;

      const offset = fetchedCounts.current[key] ?? 0;
      if (offset >= (serverTotals.current[key] ?? cached[direction].total)) return;

      // No full-canvas overlay here: revealing one page must not black out the graph the way an expansion does.
      inFlightExpansions.current.add(key);
      const stamp = expansionStamp(key);
      try {
        // Paged server-side: only one page is ever held locally, so the next batch has to be fetched.
        const page = await fetchNeighborhood(datasetId, version, system_model, pageParams(direction, offset));
        if (stamp !== expansionStamp(key)) return;
        applyPage(nodeId, direction, page[direction]);
      } catch {
        // The offset is left untouched, so the pill stays clickable and the next click retries this page.
        if (!toast.isActive(SHOW_MORE_ERROR_TOAST.id)) toast(SHOW_MORE_ERROR_TOAST);
      } finally {
        inFlightExpansions.current.delete(key);
      }
    },
    [applyPage, expansionStamp, system_model, toast, version]
  );

  const showMoreInputs = useCallback((nodeId: string) => showMore(nodeId, 'inputs'), [showMore]);

  const showMoreOutputs = useCallback((nodeId: string) => showMore(nodeId, 'outputs'), [showMore]);

  const collapseAll = useCallback(() => {
    // Bumped on the click, not in the reset effect: a page can resolve between the two.
    generation.current += 1;
    initialized.current = false;
    setResetKey((k) => k + 1);
  }, []);

  return {
    nodes,
    edges,
    nodeCounts,
    shownCounts,
    isLoading,
    isExpanding,
    rootNodeId,
    expandedNodeIds,
    expandNode,
    collapseNode,
    expandedOutputNodeIds,
    expandOutputs,
    collapseOutputs,
    collapseAll,
    showMoreInputs,
    showMoreOutputs,
  };
};
